import React from "react";

import useModal from "./modalContext";

import { Dev } from "../util/consoleUtil";

import { NotificationType, OnlyChildren } from "../../types/common";
import storageKey from "../storageKey";

type NotificationContextType = {
  broadcastData: { [key: string]: any };
  notificationIsActive: boolean;
  updateNotificationPermition: () => void;
  requestNotificationPermission: () => Promise<void>;
  notifications?: NotificationType[];
};

const NotificationContext = React.createContext<NotificationContextType | null>(null);

export function NotificationProvider({ children }: OnlyChildren) {
  const { pushAlert } = useModal();

  const [broadcastData, setBroadcastData] = React.useState<{ [key: string]: any }>({});

  const [notificationIsActive, setNotificationIsActive] = React.useState<boolean>(
    window?.Notification?.permission === "granted"
  );

  const updateNotificationPermition = () => {
    const isActive = window?.Notification?.permission === "granted";
    setNotificationIsActive(isActive);
    console.log(...new Dev("Notification permission status:", isActive).Purple());
  };

  const requestNotificationPermission = async () => {
    return new Promise<void>(async (Ok) => {
      if ("Notification" in window) {
        await window?.Notification?.requestPermission()
          .then((permission) => {
            if (permission === "granted") {
              setNotificationIsActive(true);
              console.log(...new Dev("Notification permission granted").Purple());
            }
            Ok();
          })
          .catch((err) => {
            console.warn(err);
            Ok();
          });
      } else if ("cordova" in window) {
        (window as any).FirebasePlugin.grantPermission((hasPermission: boolean) => {
          if (hasPermission) setNotificationIsActive(true);
        });
      } else {
        pushAlert({
          type: "error",
          message: "Esse dispositivo não tem suporte a notificações",
        });
        Ok();
      }
    });
  };

  /**
   * Quando a aplicação está em primeiro plano, o service worker vai
   * receber a notificação (messsage). No entanto, ele não está setado para fazer nada,
   * nesse caso, essa função diz para ele exibir um alerta quando receber
   * uma notificação em primeiro plano.
   *
   * Existe uma função presente no service worker do Firebase que lida com funções
   * em background, no entando, ela também não está setada para fazer nada, mas
   * mesmo assim ela exibe uma push notificação.
   */
  const foregroundNotificationHandler = () => {
    navigator?.serviceWorker?.addEventListener("message", (event) => {
      console.log(...new Dev("New Foreground Message", event).Info());
      pushAlert({
        type: "info",
        title: event?.data?.notification.title,
        message: event?.data?.notification.body,
      });
    });
  };

  /**
   * Atualmente essa função e o "broadcastData" não tem utilidade alguma.
   *
   * O BroadcastChannel serve para mandar uma mensagem para toda aplicação
   * mesmo que em diferentes abas. No contexto de notificações, um SW poderia mandar
   * um broadcast para um canal específico e aqui eu escutaria esse canal
   * e criaria um 'new Notification'
   */
  const handleBroadcastMessage = async () => {
    const broadcastChannel =
      "BroadcastChannel" in window ? new BroadcastChannel(storageKey.CHANNEL_NAME) : null;

    if (broadcastChannel) {
      broadcastChannel.onmessage = (event) => {
        if (event.data.alert) {
          window.alert("New Broadcast: " + event.data.alert);
        }
        console.log(...new Dev("New Broadcast Received:", event.data).Info());
        setBroadcastData(event.data);
      };
    }
  };

  React.useEffect(() => {
    foregroundNotificationHandler();

    if (storageKey.ENABLE_CHANNEL) handleBroadcastMessage();

    if ("cordova" in window) {
      (window as any).FirebasePlugin.hasPermission((hasPermission: boolean) => {
        if (hasPermission) setNotificationIsActive(true);
      });
    }
  }, []);

  const value = {
    broadcastData,
    notificationIsActive,
    updateNotificationPermition,
    requestNotificationPermission,
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
}

export default function useNotification() {
  return React.useContext(NotificationContext) as NotificationContextType;
}
